import { useRef, useState } from "react"
import {
	IconBellOutline18 as BellIcon,
	IconVolumeOutline18 as VolumeIcon,
} from "nucleo-ui-outline-18"

import { Button } from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

let sharedCtx: AudioContext | null = null
function ctx() {
	sharedCtx ??= new AudioContext()
	if (sharedCtx.state === "suspended") void sharedCtx.resume()
	return sharedCtx
}

function tone(
	freq: number,
	start: number,
	{
		type = "sine",
		decay = 0.35,
		gain = 0.25,
		detune = 0,
	}: {
		type?: OscillatorType
		decay?: number
		gain?: number
		detune?: number
	} = {},
) {
	const c = ctx()
	const t = c.currentTime + start
	const osc = c.createOscillator()
	const amp = c.createGain()
	osc.type = type
	osc.frequency.value = freq
	osc.detune.value = detune
	amp.gain.setValueAtTime(0, t)
	amp.gain.linearRampToValueAtTime(gain, t + 0.004)
	amp.gain.exponentialRampToValueAtTime(0.0001, t + decay)
	osc.connect(amp).connect(c.destination)
	osc.start(t)
	osc.stop(t + decay + 0.05)
}

// Marimba: fundamental plus a quiet 4x partial that dies fast gives the wooden knock.
function marimba(freq: number, start: number, gain = 0.3) {
	tone(freq, start, { decay: 0.45, gain })
	tone(freq * 4, start, { decay: 0.06, gain: gain * 0.35 })
}

function woodTap() {
	marimba(587.33, 0)
	marimba(880, 0.09)
}

function paperSlide() {
	const c = ctx()
	const t = c.currentTime
	const len = 0.32
	const buf = c.createBuffer(1, c.sampleRate * len, c.sampleRate)
	const data = buf.getChannelData(0)
	for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
	const src = c.createBufferSource()
	src.buffer = buf
	const filter = c.createBiquadFilter()
	filter.type = "bandpass"
	filter.Q.value = 0.9
	filter.frequency.setValueAtTime(1800, t)
	filter.frequency.exponentialRampToValueAtTime(5200, t + len)
	const amp = c.createGain()
	amp.gain.setValueAtTime(0, t)
	amp.gain.linearRampToValueAtTime(0.35, t + 0.08)
	amp.gain.exponentialRampToValueAtTime(0.0001, t + len)
	src.connect(filter).connect(amp).connect(c.destination)
	src.start(t)
	// Soft thud as the card settles.
	tone(140, len - 0.04, { decay: 0.12, gain: 0.25 })
}

const perEvent = {
	thread: () => marimba(440, 0, 0.32),
	mention: () => {
		marimba(784, 0, 0.26)
		marimba(784, 0.11, 0.22)
	},
	handoff: () => {
		marimba(523.25, 0, 0.26)
		marimba(659.25, 0.1, 0.26)
		marimba(783.99, 0.2, 0.3)
	},
}

function pluck() {
	tone(1046.5, 0, { type: "triangle", decay: 0.28, gain: 0.18, detune: -6 })
	tone(1046.5, 0, { type: "sine", decay: 0.28, gain: 0.18, detune: 6 })
	tone(2093, 0, { decay: 0.05, gain: 0.06 })
}

function motif() {
	marimba(659.25, 0, 0.24)
	marimba(493.88, 0.12, 0.24)
	marimba(987.77, 0.24, 0.28)
}

interface Sound {
	label: string
	play: () => void
	title: string
	body: string
}

const OPTIONS: { name: string; note: string; sounds: Sound[] }[] = [
	{
		name: "1. Wooden tap",
		note: "Two marimba notes, a rising fifth, about 150ms",
		sounds: [
			{
				label: "Play",
				play: woodTap,
				title: "New thread from Anna Berg",
				body: "Can I export my contacts as CSV?",
			},
		],
	},
	{
		name: "2. Paper slide",
		note: "Filtered noise sweep with a soft thud, no pitch",
		sounds: [
			{
				label: "Play",
				play: paperSlide,
				title: "New thread from Anna Berg",
				body: "Can I export my contacts as CSV?",
			},
		],
	},
	{
		name: "3. One sound per event",
		note: "Low tap, bright double tap, three-note handoff",
		sounds: [
			{
				label: "New thread",
				play: perEvent.thread,
				title: "New thread from Anna Berg",
				body: "Can I export my contacts as CSV?",
			},
			{
				label: "Mention",
				play: perEvent.mention,
				title: "Lukas mentioned you",
				body: "@Raphael can you take this one?",
			},
			{
				label: "Agent handoff",
				play: perEvent.handoff,
				title: "The agent handed you a thread",
				body: "Billing question, needs a human",
			},
		],
	},
	{
		name: "4. Synthesized pluck",
		note: "Detuned triangle pair, fast decay, built in code",
		sounds: [
			{
				label: "Play",
				play: pluck,
				title: "New thread from Anna Berg",
				body: "Can I export my contacts as CSV?",
			},
		],
	},
	{
		name: "5. Brand motif",
		note: "Three notes we reuse on every surface",
		sounds: [
			{
				label: "Play",
				play: motif,
				title: "New thread from Anna Berg",
				body: "Can I export my contacts as CSV?",
			},
		],
	},
]

function Banner({ sound, shownAt }: { sound: Sound; shownAt: number }) {
	return (
		<div
			key={shownAt}
			className="flex w-full animate-[banner-in_260ms_cubic-bezier(0.2,0.9,0.3,1)] items-start gap-3 rounded-xl bg-white/90 p-3 shadow-lg ring-1 ring-black/5 backdrop-blur dark:bg-gray-800/90 dark:ring-white/10"
		>
			<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black text-white dark:bg-white dark:text-black">
				<BellIcon className="h-4 w-4" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-center justify-between gap-2">
					<div className="truncate text-[13px] font-semibold text-primary">
						{sound.title}
					</div>
					<div className="shrink-0 text-[11px] text-secondary">now</div>
				</div>
				<div className="truncate text-[13px] text-secondary">{sound.body}</div>
			</div>
		</div>
	)
}

function OptionCard({ option }: { option: (typeof OPTIONS)[number] }) {
	const [shown, setShown] = useState<{ sound: Sound; at: number } | null>(null)
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

	const fire = (sound: Sound) => {
		sound.play()
		setShown({ sound, at: Date.now() })
		clearTimeout(timer.current)
		timer.current = setTimeout(() => setShown(null), 2600)
	}

	return (
		<div className="flex w-[360px] flex-col gap-2">
			<div>
				<div className="text-sm font-medium text-primary">{option.name}</div>
				<div className="text-xs text-secondary">{option.note}</div>
			</div>
			<div className="flex h-[220px] flex-col justify-between overflow-hidden rounded-lg border border-gray-200 bg-gradient-to-b from-gray-100 to-gray-50 p-3 dark:border-gray-700/80 dark:from-gray-850 dark:to-gray-900">
				<div className="h-[62px]">
					{shown && <Banner sound={shown.sound} shownAt={shown.at} />}
				</div>
				<div className="flex flex-wrap gap-2">
					{option.sounds.map((s) => (
						<Button
							key={s.label}
							variant="outline"
							Icon={VolumeIcon}
							onClick={() => fire(s)}
						>
							{s.label}
						</Button>
					))}
				</div>
			</div>
		</div>
	)
}

export function NotificationSoundsPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<style>{`@keyframes banner-in { from { opacity: 0; transform: translateY(-10px) scale(0.98) } to { opacity: 1; transform: none } }`}</style>
			<div className="mx-auto max-w-[1280px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Notification sounds, five options
					</div>
					<div className="text-sm text-secondary">
						Each button plays the sound and shows the banner it would come with.
					</div>
				</div>
				<div className="flex flex-wrap gap-6">
					{OPTIONS.map((o) => (
						<OptionCard key={o.name} option={o} />
					))}
				</div>
			</div>
		</div>
	)
}
